import { SYSTEM_DISPLAY_TIME_ZONE } from '@pillar-portal/api-types';

/**
 * datetime-local inputs that read and write wall-clock time in
 * SYSTEM_DISPLAY_TIME_ZONE rather than in the browser's own zone, so a date
 * an administrator enters means the same instant wherever they are.
 */

const wallClockPattern: RegExp = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const wallClockFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: SYSTEM_DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});

type WallClock = {
    readonly year: number;
    readonly month: number;
    readonly day: number;
    readonly hour: number;
    readonly minute: number;
    readonly second: number;
};

function wallClockAt(instant: Date): WallClock {
    const parts: Map<string, number> = new Map(
        wallClockFormatter
            .formatToParts(instant)
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, Number(part.value)])
    );
    const part = (type: string): number => parts.get(type) ?? 0;
    return {
        year: part('year'),
        month: part('month'),
        day: part('day'),
        hour: part('hour'),
        minute: part('minute'),
        second: part('second'),
    };
}

/** Milliseconds the system zone is ahead of UTC at the given instant. */
function systemZoneOffsetAt(instant: Date): number {
    const wallClock: WallClock = wallClockAt(instant);
    const wallClockAsUtc: number = Date.UTC(
        wallClock.year,
        wallClock.month - 1,
        wallClock.day,
        wallClock.hour,
        wallClock.minute,
        wallClock.second
    );
    return wallClockAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

function pad(value: number): string {
    return value.toString().padStart(2, '0');
}

/** The zone's name for labels, e.g. "Eastern Time". */
export const SYSTEM_TIME_ZONE_LABEL: string =
    new Intl.DateTimeFormat('en-US', {
        timeZone: SYSTEM_DISPLAY_TIME_ZONE,
        timeZoneName: 'longGeneric',
    })
        .formatToParts(new Date(0))
        .find((part) => part.type === 'timeZoneName')?.value ?? SYSTEM_DISPLAY_TIME_ZONE;

/** An instant as a datetime-local value in the system zone, to the minute. */
export function toSystemDateTimeLocalInput(isoTimestamp: string): string {
    const wallClock: WallClock = wallClockAt(new Date(isoTimestamp));
    return `${wallClock.year}-${pad(wallClock.month)}-${pad(wallClock.day)}T${pad(wallClock.hour)}:${pad(wallClock.minute)}`;
}

/** The current minute as a datetime-local value in the system zone. */
export function nowForSystemDateTimeLocalInput(): string {
    return toSystemDateTimeLocalInput(new Date().toISOString());
}

/**
 * Reads a datetime-local value as wall-clock time in the system zone and
 * returns the instant as an ISO timestamp, or null when it is not a valid
 * wall-clock time. A time skipped by a daylight-saving change resolves to the
 * instant an hour earlier.
 */
export function fromSystemDateTimeLocalInput(value: string): string | null {
    const match: RegExpExecArray | null = wallClockPattern.exec(value);
    if (match === null) {
        return null;
    }
    const year: number = Number(match[1]);
    const month: number = Number(match[2]);
    const day: number = Number(match[3]);
    const hour: number = Number(match[4]);
    const minute: number = Number(match[5]);
    const wallClockAsUtc: number = Date.UTC(year, month - 1, day, hour, minute);
    if (Number.isNaN(wallClockAsUtc) || new Date(wallClockAsUtc).getUTCDate() !== day) {
        return null;
    }
    const firstGuess: number = wallClockAsUtc - systemZoneOffsetAt(new Date(wallClockAsUtc));
    const instant: number = wallClockAsUtc - systemZoneOffsetAt(new Date(firstGuess));
    return new Date(instant).toISOString();
}
